import { Add, ArrowBack } from "@mui/icons-material";
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  type ManagedMission,
  type Mission,
} from "@rescuetablet/missions-client";
import { nanoid } from "nanoid";
import { useCallback, useMemo } from "react";
import { Outlet, useNavigate, useOutletContext, useParams } from "react-router";
import LinkBehavior from "./LinkBehavior";

export default function MissionPage() {
  const { missionId } = useParams<{ missionId: string }>();
  const { missions } = useOutletContext<{
    missions: ReadonlyArray<ManagedMission>;
  }>();
  const mission = useMemo(
    () => missions.find((m) => m.id === missionId),
    [missionId, missions]
  );

  if (!mission) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" component={LinkBehavior} href="..">
            zurück zur Liste
          </Button>
        }
      >
        Einsatz nicht gefunden
      </Alert>
    );
  }

  if (mission.state === "loading") return <LinearProgress />;
  if (mission.state === "error") {
    return <Alert severity="error">{mission.error.message}</Alert>;
  }

  return <Content mission={mission.mission} />;
}

function Content({ mission }: { mission: Mission }) {
  const navigate = useNavigate();
  const createPatient = useCallback(
    () => navigate(`patients/${nanoid()}`),
    [navigate]
  );

  return (
    <Stack spacing={1}>
      <Card>
        <CardContent>
          <Typography variant="h5">
            {[mission.keyword, mission.message].filter(Boolean).join(" - ")}
          </Typography>
          {mission.address && (
            <Typography variant="caption">{mission.address}</Typography>
          )}
        </CardContent>
        <CardActions>
          <Button
            component={LinkBehavior}
            variant="text"
            href=".."
            startIcon={<ArrowBack />}
          >
            Zurück zur Liste der Einsätze
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={createPatient}
          >
            Neuer Patient
          </Button>
        </CardActions>
      </Card>
      <Outlet context={{ mission }} />
    </Stack>
  );
}
